import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';

import type { WithTestId } from 'types/app';

import styles from './style.module.scss';

const SCRIPT_SRC = 'https://dcdn.veonadx.com/banner.js';
// 300x250 medium rectangle ad unit provided by Veon Adtech.
const DEFAULT_AD_UNIT_ID = 'birbir_300x250';

export type VeonAdBannerProps = WithTestId<{
  adUnitId?: string;
  className?: string;
}>;

export const VeonAdBanner = ({
  adUnitId = DEFAULT_AD_UNIT_ID,
  className,
  testId = 'veon-ad-banner',
}: VeonAdBannerProps) => {
  const { t } = useTranslation();
  const slotRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const slot = slotRef.current;
    if (!slot) {
      return undefined;
    }

    // The loader reads document.currentScript and inserts the creative as the
    // script's next sibling, so we mount the script inside our slot element.
    const script = document.createElement('script');
    script.src = SCRIPT_SRC;
    script.setAttribute('data-ad', 'veon');
    script.setAttribute('adunit-id', adUnitId);
    // If the loader 404s or is blocked (e.g. ad blocker), empty the slot so the
    // reserved box does not keep a dead script around. The box stays reserved to
    // avoid CLS; only the badge remains visible.
    script.onerror = () => {
      slot.replaceChildren();
    };

    slot.appendChild(script);

    return () => {
      // Drop the script and the injected creative on unmount / ad unit change.
      slot.replaceChildren();
    };
  }, [adUnitId]);

  return (
    <div className={cn(styles.root, className)} data-testid={testId}>
      <div ref={slotRef} className={styles.slot} data-testid="slot" />
      <div className={styles.badge}>{t('badge.advertisement')}</div>
    </div>
  );
};
